import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/**
 * First-party consent manager. No third-party CMP, nothing leaves the browser.
 *
 * Non-essential scripts (currently only Sentry) must not load before the
 * visitor opts in: §25(1) TDDDG and Art. 6(1)(a) GDPR. Consumers of this
 * context are responsible for honouring `consent.analytics`.
 */

export type ConsentCategory = "necessary" | "analytics";

export type ConsentState = {
  necessary: true;
  analytics: boolean;
};

const STORAGE_KEY = "ss_consent";

/**
 * Bump when the set of categories or the processors behind them changes. A
 * stored decision from an older version is treated as absent, so the visitor is
 * asked again rather than silently opted in to something new.
 *
 * v2: removed the "support" category along with the Crisp live-chat integration.
 */
const CONSENT_VERSION = 2;

type StoredConsent = {
  v: number;
  analytics: boolean;
  at: string;
};

export const DENY_ALL: ConsentState = {
  necessary: true,
  analytics: false,
};

const ACCEPT_ALL: ConsentState = {
  necessary: true,
  analytics: true,
};

type ConsentContextValue = {
  consent: ConsentState;
  /** False until a stored decision is read, or the visitor makes one. */
  hasDecided: boolean;
  /** True once the provider has read localStorage; guards SSR/hydration. */
  isHydrated: boolean;
  bannerOpen: boolean;
  preferencesOpen: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  save: (next: Omit<ConsentState, "necessary">) => void;
  openPreferences: () => void;
  closePreferences: () => void;
};

const ConsentContext = createContext<ConsentContextValue | undefined>(
  undefined,
);

function read(): StoredConsent | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;
    if (parsed?.v !== CONSENT_VERSION) return null;
    if (typeof parsed.analytics !== "boolean") return null;
    return parsed;
  } catch {
    return null;
  }
}

function write(state: ConsentState) {
  try {
    const payload: StoredConsent = {
      v: CONSENT_VERSION,
      analytics: state.analytics,
      at: new Date().toISOString(),
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Private mode, or storage disabled. Consent then lasts for the session
    // only, which is the privacy-safe direction to fail in.
  }
}

export function ConsentProvider({ children }: { children: React.ReactNode }) {
  // Always start denied. Server and first client render must agree, and an
  // unknown decision must never mean "allowed".
  const [consent, setConsent] = useState<ConsentState>(DENY_ALL);
  const [hasDecided, setHasDecided] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  useEffect(() => {
    const stored = read();
    if (stored) {
      setConsent({ necessary: true, analytics: stored.analytics });
      setHasDecided(true);
    }
    setIsHydrated(true);
  }, []);

  const commit = useCallback(
    (next: ConsentState) => {
      write(next);
      setPreferencesOpen(false);

      // Sentry cannot be fully torn down once booted: it installs globals,
      // handlers and listeners. Unmounting our wrapper is not enough. If the
      // category is being withdrawn after having been granted, do a hard reload
      // so the visitor genuinely gets a page with that script absent, which is
      // what Art. 7(3) GDPR promises them.
      const withdrawing = consent.analytics && !next.analytics;

      if (withdrawing && typeof window !== "undefined") {
        window.location.reload();
        return;
      }

      setConsent(next);
      setHasDecided(true);
    },
    [consent.analytics],
  );

  const acceptAll = useCallback(() => commit(ACCEPT_ALL), [commit]);
  const rejectAll = useCallback(() => commit(DENY_ALL), [commit]);

  const save = useCallback(
    (next: Omit<ConsentState, "necessary">) =>
      commit({ necessary: true, ...next }),
    [commit],
  );

  const value = useMemo<ConsentContextValue>(
    () => ({
      consent,
      hasDecided,
      isHydrated,
      // Never render the banner during SSR: it would flash for visitors who
      // already decided.
      bannerOpen: isHydrated && !hasDecided,
      preferencesOpen,
      acceptAll,
      rejectAll,
      save,
      openPreferences: () => setPreferencesOpen(true),
      closePreferences: () => setPreferencesOpen(false),
    }),
    [
      consent,
      hasDecided,
      isHydrated,
      preferencesOpen,
      acceptAll,
      rejectAll,
      save,
    ],
  );

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  );
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error("useConsent must be used within a ConsentProvider");
  return ctx;
}
